import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, count, sum } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { REPORT } from '@consts/reports';
import { chart } from '@utils/charts';
import { getUniqueDates, groupBy } from '@utils/data';
import { styleDate, styleDateRange } from '@utils/styles';

interface FullBodyPercentByMonthProps {
    dateRange: { startDate: string; endDate: string };
}

export const FullBodyPercentByMonth = ({ dateRange }: FullBodyPercentByMonthProps) => {
    const [range, setRange] = useRange('FullBodyPercentByMonth');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.PRODUCTS_OVERALL_FULL_BODY_RATE,
        params: { range, ...dateRange },
    });

    const dateKeys = getUniqueDates(data, range);

    const groupedByDate = groupBy(data, 'date');

    const fullBody = { name: 'Full Body %', data: dateKeys.map(({ date }) => groupedByDate[date]?.[0]?.fullBodyPct) };
    const globalAvg = { name: 'Global Avg', data: dateKeys.map(({ date }) => groupedByDate[date]?.[0]?.globalAvg) };
    const globalMax = { name: 'Global Max', data: dateKeys.map(({ date }) => groupedByDate[date]?.[0]?.globalMax) };

    return (
        <BaseReport
            setRange={setRange}
            title={'Full Body % Rate by Month'}
            description={
                'This graph shows your overall Full Body coverage rate by month, allowing you to compare it to the global average and the top performer.'
            }
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            range={range}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers: ['Date', 'Fully Body %', 'Global Avg', 'Global Max'],
                data: data.map((item) => [item.date, item.fullBodyPct, item.globalAvg, item.globalMax]),
                summaries: [count, average, average, sum],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort, basicSort],
            }}
            chartOptions={chart(300, [{ name: 'Full Body %', opposite: false }], dateKeys, [
                fullBody,
                globalAvg,
                globalMax,
            ])}
        />
    );
};
